// What live's staged templates' forms share in their footer (spec G14-drafts; live's shared
// `submit-button` and `subtitles-popover`): the round ↑ that names what it waits for, and the
// subtitles chip (Podcast Ad's, Street Interview Ad's).
import { ActionIcon, Popover, SegmentedControl, Stack, Switch, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { ArrowUp, Captions } from "lucide-react";
import { useState } from "react";

import type { VideoCaptionPosition } from "../../../api/generated/model";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const SUBTITLES_TOOLTIP =
  "Burned-in captions: turn them on or off, pick where they sit and how they look.";
export type CaptionPosition = VideoCaptionPosition;
/** Live's subtitle places (`subtitles-popover/constants.ts`). */
const POSITIONS: { value: CaptionPosition; label: string }[] = [
  { value: "top", label: "Top" },
  { value: "center", label: "Center" },
  { value: "bottom", label: "Bottom" },
];

export interface SubmitProps {
  label: string;
  /** What it waits for, one a line in its tooltip while it is off. */
  reasons: readonly string[];
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
}

/** Live's round ↑ (`SubmitButton`): its tooltip names what is missing, else what it does. */
export function Submit({ label, reasons, disabled, loading, onClick }: SubmitProps) {
  const lines = disabled && reasons.length > 0 ? reasons : [label];
  return (
    <Tooltip
      label={
        <>
          {lines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </>
      }
      withArrow
    >
      {/* A disabled button takes no hover, so the tooltip hangs from a wrapper. */}
      <span className={classes.submit}>
        <ActionIcon
          size="lg"
          radius="100%"
          variant="filled"
          aria-label={label}
          loading={loading}
          disabled={disabled}
          onClick={onClick}
        >
          <ArrowUp size={20} strokeWidth={1.5} />
        </ActionIcon>
      </span>
    </Tooltip>
  );
}

export interface SubtitlesProps {
  on: boolean;
  position: CaptionPosition;
  disabled: boolean;
  onChange: (change: { subtitles?: boolean; caption_position?: CaptionPosition }) => void;
  /** The template's own tooltip where live words it otherwise (Pixar Talking Ad's). */
  tooltip?: string;
}

/**
 * Live's subtitles chip (`subtitles-popover`, capture 124): "On, Bottom" or "Off", its popover
 * the switch and the place. Live's caption styles come with Promote Your Product's shared picker
 * and its styles route (G14 PR 15), which PR B puts here.
 */
export function Subtitles({ on, position, disabled, onChange, tooltip = SUBTITLES_TOOLTIP }: SubtitlesProps) {
  const [opened, setOpened] = useState(false);
  const place = POSITIONS.find((each) => each.value === position)?.label ?? "Bottom";
  const words = on ? `On, ${place}` : "Off";
  return (
    <Popover opened={opened} onChange={setOpened} position="top-start" withinPortal={false}>
      <Popover.Target>
        <Tooltip label={tooltip} multiline maw={280} withArrow disabled={opened}>
          <UnstyledButton
            className={classes.setting}
            aria-label={`Subtitles: ${words}`}
            disabled={disabled}
            onClick={() => setOpened((open) => !open)}
          >
            <Captions size={16} strokeWidth={1.5} aria-hidden />
            <span>{words}</span>
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown className={classes.subtitles}>
        <Stack gap={12}>
          <Switch
            label="Subtitles"
            labelPosition="left"
            checked={on}
            onChange={(event) => onChange({ subtitles: event.currentTarget.checked })}
          />
          <Text size="sm" {...(on ? {} : { c: "dimmed" })}>
            Position
          </Text>
          <SegmentedControl
            aria-label="Position"
            data={POSITIONS}
            value={position}
            disabled={!on}
            onChange={(next) => {
              const chosen = POSITIONS.find((each) => each.value === next);
              if (chosen !== undefined) onChange({ caption_position: chosen.value });
            }}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
